import { albumBuilderConstants as C } from "@/actions/constants/albumBuilderConstants";
import { HistoryManager } from "@/engine/album/managers/HistoryManager";

const TRACKED = new Set<string>(
    [
        C.moveNode,
        C.resizeNode,
        C.rotateImage,
        C.setNodeOpacity,
        C.updateNodeStyle,
        C.updateCrop,
        C.changeAsset,
        C.reorderNodes,
        C.addGrid,
        C.splitSlotV,
        C.splitSlotH,
        C.expandSlot,
        C.resizeSiblingSlots,
        C.deleteSlot,
        C.cutSlotImages,
        C.pasteSlotImages,
        C.swapSlotImages,
        C.setBackgroundColor,
        C.setBackgroundAsset,
        C.setBackgroundOpacity,
        C.renameSheet,
        C.applyPages,
    ].filter((t): t is string => typeof t === "string" && t.length > 0),
);

const clone = <T,>(v: T): T => {
    if (typeof structuredClone === "function") return structuredClone(v);
    return JSON.parse(JSON.stringify(v));
};

const getPayloadPages = (payload: any): any[] => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.pages)) return payload.pages;
    return [];
};

const getPageId = (page: any): string | null => {
    const id = page?.id ?? page?.pageId;
    return typeof id === "string" && id.length > 0 ? id : null;
};

export const albumBuilderHistoryMiddleware =
    (store: any) => (next: any) => (action: any) => {
        const type = action?.type;
        if (typeof type !== "string") return next(action);

        const isReplay = !!action?.meta?.historyReplay;
        const isTracked = TRACKED.has(type);

        if (!isTracked || isReplay) return next(action);

        const prev = store.getState().albumBuilder;

        let pageIds: string[] = [];

        if (type === C.applyPages) {
            pageIds = getPayloadPages(action?.payload)
                .map(getPageId)
                .filter((id): id is string => !!id);
        } else {
            const pageId = action?.payload?.pageId;
            if (typeof pageId === "string" && pageId.length > 0) {
                pageIds = [pageId];
            }
        }

        if (!pageIds.length) return next(action);

        const uniquePageIds = Array.from(new Set(pageIds));
        const prevPagesById = Object.fromEntries(
            uniquePageIds.map((id) => [id, clone(prev.pages?.[id])]),
        );

        const result = next(action);

        const curr = store.getState().albumBuilder;
        const changedPageIds = uniquePageIds.filter((id) => {
            const before = prevPagesById[id];
            const after = clone(curr.pages?.[id]);
            if (!before || !after) return false;
            return JSON.stringify(before) !== JSON.stringify(after);
        });

        if (!changedPageIds.length) return result;

        if (typeof C.applyPages !== "string" || !C.applyPages.length) {
            return result;
        }

        const undoPages = changedPageIds.map((id) => prevPagesById[id]);
        const redoPages = changedPageIds.map((id) => clone(curr.pages?.[id]));

        HistoryManager.push({
            id: `${type}-${changedPageIds.join(",")}-${Date.now()}`,
            label: type.replace("ALBUM_BUILDER_", "").toLowerCase(),
            timestamp: Date.now(),
            patches: [
                {
                    undo: {
                        type: C.applyPages,
                        payload: undoPages,
                        meta: { historyReplay: true },
                    },
                    redo: {
                        type: C.applyPages,
                        payload: redoPages,
                        meta: { historyReplay: true },
                    },
                },
            ],
        });

        return result;
    };